import {
  addYears,
  differenceInDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachWeekOfInterval,
  endOfDay,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  getDaysInMonth,
  getDaysInYear,
  getWeek,
  startOfDay,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { weekdaysNumberMap } from 'shared/constants';
import { DateRange } from 'shared/types/date-range';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { TimelineTimeframes } from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { useTheme } from 'styled-components';

import { CategoryListItem } from './category-list-item';
import { rowHeight } from './constants';
import { GoalBar } from './goal-bar';
import * as Styled from './goal-timeline.style';
import { Placeholder } from './placeholder';
import { TodayLine } from './today-line';
import {
  Category,
  DragState,
  ReorderPositions,
  SideBarItem,
  SubCategory,
} from './types';

type PlaceholderState = {
  visible: boolean;
  dayIndex: number;
  goalId: string | null;
};

export type GoalTimelineProps = {
  categories: Category[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  dateRange: DateRange;
  onUpdateDates: (
    goalId: ID,
    newStartDate: Timestamp,
    newEndDate: Timestamp,
  ) => void;
  onReorderGoals: (
    draggedId: ID,
    targetId: ID,
    position: ReorderPositions,
  ) => void;
  collapsedItems: ID[];
  toggleCollapse: (id: ID) => void;
  onCategoryAdd?: (id: ID) => void;
  timeframe: TimelineTimeframes;
  referenceDate: Date;
  weekStartsOn: WeekDays;
  title: string;
};

const timeframeColumnFuncMap: Record<
  TimelineTimeframes,
  {
    columns: (dateRange: DateRange, weekStartsOn: WeekDays) => Timestamp[];
    key: (date: Timestamp) => string;
    label: (date: Timestamp, weekStartsOn: WeekDays) => string;
    width: (date: Timestamp, dayWidth: number) => number;
    isStartOfYear: (date: Timestamp, weekStartsOn: WeekDays) => boolean;
  }
> = {
  [TimelineTimeframes.Month]: {
    columns: (dateRange, weekStartsOn) =>
      eachWeekOfInterval(
        {
          start: dateRange.startDate,
          end: dateRange.endDate,
        },
        { weekStartsOn: weekdaysNumberMap[weekStartsOn] },
      ),
    key: (date) => format(date, 'yyyy-MM-dd'),
    width: (date, dayWidth) => dayWidth * 7,
    label: (date, weekStartsOn) =>
      getWeek(date, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }) === 1
        ? format(
            endOfWeek(date, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }),
            'yyyy',
          )
        : format(date, 'LLL d'),
    isStartOfYear: (date, weekStartsOn) =>
      getWeek(date, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }) === 1,
  },
  [TimelineTimeframes.Quarter]: {
    columns: (dateRange) =>
      eachMonthOfInterval({
        start: dateRange.startDate,
        end: dateRange.endDate,
      }),
    key: (date) => format(date, 'yyyy-MM-dd'),
    width: (date, dayWidth) => dayWidth * getDaysInMonth(date),
    label: (date) => format(date, date.getMonth() === 0 ? 'yyyy' : 'MMM'),
    isStartOfYear: (date) => date.getMonth() === 0,
  },
  [TimelineTimeframes.Year]: {
    columns: (dateRange) =>
      eachMonthOfInterval({
        start: dateRange.startDate,
        end: dateRange.endDate,
      }),
    key: (date) => format(date, 'yyyy-MM-dd'),
    width: (date, dayWidth) => dayWidth * getDaysInMonth(date),
    label: (date) => format(date, date.getMonth() === 0 ? 'yyyy' : 'MMM'),
    isStartOfYear: (date) => date.getMonth() === 0,
  },
  [TimelineTimeframes.FiveYears]: {
    columns: (dateRange) =>
      eachQuarterOfInterval({
        start: dateRange.startDate,
        end: dateRange.endDate,
      }),
    key: (date) => format(date, 'yyyy-MM-dd'),
    width: (date, dayWidth) =>
      dayWidth *
      eachDayOfInterval({
        start: startOfQuarter(date),
        end: endOfQuarter(date),
      }).length,
    label: (date) => (date.getMonth() === 0 ? format(date, 'yyyy') : ''),
    isStartOfYear: (date) => date.getMonth() === 0,
  },
};

const timeframeDaysFuncMap: Record<
  TimelineTimeframes,
  (date: Timestamp) => number
> = {
  [TimelineTimeframes.Month]: getDaysInMonth,
  [TimelineTimeframes.Quarter]: (date) =>
    differenceInDays(endOfQuarter(date), startOfQuarter(date)),
  [TimelineTimeframes.Year]: getDaysInYear,
  [TimelineTimeframes.FiveYears]: (date) =>
    differenceInDays(
      endOfYear(addYears(date, 4)),
      startOfYear(addYears(date, -1)),
    ),
};

const PLACEHOLDER_DURATIONS_DAYS = {
  [TimelineTimeframes.Month]: 14,
  [TimelineTimeframes.Quarter]: 30,
  [TimelineTimeframes.Year]: 30,
  [TimelineTimeframes.FiveYears]: 182,
};

// Helper to convert rem string to px number
const remToPx = (remString: string) => parseFloat(remString) * 10;

export const GoalTimeline = React.forwardRef<HTMLDivElement, GoalTimelineProps>(
  (
    {
      categories,
      goals,
      lifeAreas,
      dateRange,
      onUpdateDates,
      onReorderGoals,
      collapsedItems,
      toggleCollapse,
      onCategoryAdd,
      timeframe,
      referenceDate,
      weekStartsOn,
      title,
    },
    ref,
  ) => {
    const [placeholderState, setPlaceholderState] = useState<PlaceholderState>({
      visible: false,
      dayIndex: 0,
      goalId: null,
    });
    const [dragState, setDragState] = useState<DragState>({
      draggedId: null,
      dropTargetId: null,
      dropPosition: null,
    });
    const [containerWidth, setContainerWidth] = useState(0);

    const leftBodyRef = useRef<HTMLDivElement>(null);
    const rightHeaderRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();

    const { totalDays, dayWidth } = useMemo(() => {
      const totalDays = differenceInDays(
        dateRange.endDate,
        dateRange.startDate,
      );
      const daysInView = timeframeDaysFuncMap[timeframe](referenceDate);
      const calculatedDayWidth =
        containerWidth > 0
          ? Math.floor((containerWidth / daysInView) * 100) / 100
          : 0;

      return { totalDays, dayWidth: calculatedDayWidth };
    }, [
      containerWidth,
      dateRange.endDate,
      dateRange.startDate,
      referenceDate,
      timeframe,
    ]);

    const timelineGridWidth = totalDays * dayWidth;

    useEffect(() => {
      if (!ref || typeof ref === 'function' || !ref.current) {
        return;
      }
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          setContainerWidth(entries[0].contentRect.width);
        }
      });
      resizeObserver.observe(ref.current);
      return () => resizeObserver.disconnect();
    }, [ref]);

    useEffect(() => {
      if (!ref || typeof ref === 'function' || !ref.current || dayWidth <= 0) {
        return;
      }

      const startOfTimeline = dateRange.startDate;
      const offsetDays =
        (referenceDate.getTime() - startOfTimeline.getTime()) /
        (1000 * 60 * 60 * 24);

      ref.current.scrollTo({
        left: offsetDays * dayWidth,
        behavior: 'auto',
      });
    }, [referenceDate, dayWidth, timeframe, ref, dateRange.startDate]);

    const handleScroll = () => {
      if (!ref || typeof ref === 'function' || !ref.current) {
        return;
      }
      const { scrollTop, scrollLeft } = ref.current;
      if (leftBodyRef.current) {
        leftBodyRef.current.scrollTop = scrollTop;
      }
      if (rightHeaderRef.current) {
        rightHeaderRef.current.scrollLeft = scrollLeft;
      }
    };

    const flattenedRows = useMemo(() => {
      const result: SideBarItem[] = [];
      categories.forEach((category) => {
        result.push({
          id: category.id,
          lifeArea: category.lifeArea,
          hasGoals: category.subCategories.length > 0,
          level: 0,
        });

        if (!collapsedItems.includes(category.id)) {
          const flatten = (subCategories: SubCategory[], level: number) => {
            subCategories.forEach((subCategory) => {
              const goal = subCategory.goal;
              result.push({
                id: goal.id,
                level,
                goal,
                hasGoals: !!subCategory.subCategories.length,
              });

              if (!collapsedItems.includes(goal.id)) {
                flatten(subCategory.subCategories, level + 1);
              }
            });
          };

          flatten(category.subCategories, 0);
        }
      });
      return result;
    }, [categories, collapsedItems]);

    // Calculate top positions for each row, accounting for extra spacing above categories (except the first)
    const rowTops = useMemo(() => {
      const tops: number[] = [];
      let currentTop = remToPx(theme.spacing(2.5));
      let isFirstCategory = true;

      flattenedRows.forEach((item) => {
        // Add extra spacing above categories except the first
        if ('lifeArea' in item) {
          if (!isFirstCategory) {
            currentTop += remToPx(theme.spacing(4));
          }
          isFirstCategory = false;
        }
        tops.push(currentTop);
        currentTop += rowHeight;
      });

      return tops;
    }, [flattenedRows, theme]);

    // Calculate the total height of all rows (including extra category spacing)
    const totalRowsHeight = useMemo(() => {
      if (rowTops.length === 0) {
        return 0;
      }
      return rowTops[rowTops.length - 1] + rowHeight;
    }, [rowTops]);

    const handleDragStart = (e: React.DragEvent, goalId: string) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', goalId);
      setDragState((prev) => ({ ...prev, draggedId: goalId }));
    };

    const handleDragEnd = () => {
      setDragState({ draggedId: null, dropTargetId: null, dropPosition: null });
    };

    const handleDragLeave = () => {
      setDragState((prev) => ({
        ...prev,
        dropTargetId: null,
        dropPosition: null,
      }));
    };

    const handleDragOver = (e: React.DragEvent, item: SideBarItem) => {
      e.preventDefault();
      if (item.id === dragState.draggedId) {
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      let position: ReorderPositions =
        relativeY < rect.height * 0.5
          ? ReorderPositions.Before
          : ReorderPositions.After;

      if (
        'goal' in item &&
        relativeY > rect.height * 0.25 &&
        relativeY < rect.height * 0.75
      ) {
        position = ReorderPositions.Inside;
      } else if ('lifeArea' in item) {
        position = ReorderPositions.InsideCategory;
      }

      setDragState((prev) => ({
        ...prev,
        dropTargetId: item.id,
        dropPosition: position,
      }));
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      if (
        dragState.draggedId &&
        dragState.dropTargetId &&
        dragState.dropPosition
      ) {
        onReorderGoals(
          dragState.draggedId,
          dragState.dropTargetId,
          dragState.dropPosition,
        );
      }
      handleDragEnd();
    };

    const handleTimelineMouseMove = (e: React.MouseEvent) => {
      if (!ref || typeof ref === 'function' || !ref.current || dayWidth <= 0) {
        return;
      }

      const timelineRect = ref.current.getBoundingClientRect();
      const mouseY = e.clientY - timelineRect.top + ref.current.scrollTop;

      // We iterate through all possible rows to find which one the mouse is hovering over.
      // We can't use a simple division by rowHeight, because categories have extra vertical spacing.
      const rowIndex = rowTops.findIndex((rowTop) => {
        const rowBottom = rowTop + rowHeight;
        return mouseY >= rowTop && mouseY <= rowBottom;
      });

      // If no row is hovered, hide the placeholder.
      if (rowIndex === -1) {
        setPlaceholderState({ visible: false, dayIndex: 0, goalId: null });
        return;
      }

      const item = flattenedRows[rowIndex];

      if (
        item &&
        'goal' in item &&
        (!item.goal.startDate || !item.goal.deadline)
      ) {
        const x = e.clientX - timelineRect.left + ref.current.scrollLeft;
        setPlaceholderState({
          visible: true,
          dayIndex: Math.round(x / dayWidth),
          goalId: item.id,
        });
        return;
      }

      setPlaceholderState({ visible: false, dayIndex: 0, goalId: null });
    };

    const handleTimelineMouseLeave = () =>
      setPlaceholderState({ visible: false, dayIndex: 0, goalId: null });

    const handleTimelineClick = () => {
      if (!placeholderState.visible || !placeholderState.goalId) {
        return;
      }

      const durationDays = PLACEHOLDER_DURATIONS_DAYS[timeframe];
      let startDate = new Date(dateRange.startDate);
      startDate.setDate(
        startDate.getDate() +
          placeholderState.dayIndex -
          Math.round(durationDays / 2),
      );
      startDate = startOfDay(startDate);

      let endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (durationDays - 1));
      endDate = endOfDay(endDate);

      onUpdateDates(placeholderState.goalId, startDate, endDate);
    };

    const columns = useMemo(
      () => timeframeColumnFuncMap[timeframe].columns(dateRange, weekStartsOn),
      [dateRange, timeframe, weekStartsOn],
    );

    const HeaderContent = useMemo(() => {
      const columnFuncs = timeframeColumnFuncMap[timeframe];

      return (
        <Styled.MonthsHeaderContainer>
          {columns.map((column) => (
            <Styled.MonthHeader
              key={columnFuncs.key(column)}
              $width={columnFuncs.width(column, dayWidth)}
              $isStartOfYear={columnFuncs.isStartOfYear(column, weekStartsOn)}
            >
              {columnFuncs.label(column, weekStartsOn)}
            </Styled.MonthHeader>
          ))}
        </Styled.MonthsHeaderContainer>
      );
    }, [columns, dayWidth, timeframe, weekStartsOn]);

    const MonthBackground = React.memo(() => {
      const columnFuncs = timeframeColumnFuncMap[timeframe];

      return (
        <Styled.MonthContainer>
          {columns.map((column) => (
            <Styled.MonthBg
              key={columnFuncs.key(column)}
              $width={columnFuncs.width(column, dayWidth)}
            />
          ))}
        </Styled.MonthContainer>
      );
    });

    return (
      <Styled.Container>
        <Styled.HeaderRow>
          <Styled.GoalListHeader>
            <Styled.GoalListTitle>{title}</Styled.GoalListTitle>
          </Styled.GoalListHeader>
          <Styled.TimelineHeader ref={rightHeaderRef}>
            <Styled.TimelineHeaderContent
              $timelineGridWidth={timelineGridWidth}
            >
              {HeaderContent}
            </Styled.TimelineHeaderContent>
          </Styled.TimelineHeader>
        </Styled.HeaderRow>

        <Styled.BodyContainer>
          <Styled.Body>
            <Styled.GoalListBody ref={leftBodyRef}>
              <Styled.CategoryList $height={totalRowsHeight}>
                {flattenedRows.map((item) => (
                  <CategoryListItem
                    key={item.id}
                    item={item}
                    goals={goals}
                    lifeAreas={lifeAreas}
                    dragState={dragState}
                    handleDragOver={handleDragOver}
                    handleDrop={handleDrop}
                    handleDragLeave={handleDragLeave}
                    handleDragStart={handleDragStart}
                    handleDragEnd={handleDragEnd}
                    toggleCollapse={toggleCollapse}
                    collapsedItems={collapsedItems}
                    onCategoryAdd={onCategoryAdd}
                  />
                ))}
              </Styled.CategoryList>
            </Styled.GoalListBody>

            <Styled.TimelineBody
              ref={ref}
              onScroll={handleScroll}
              onMouseMove={handleTimelineMouseMove}
              onMouseLeave={handleTimelineMouseLeave}
              onClick={handleTimelineClick}
            >
              <Styled.TimelineGrid
                $timelineGridWidth={timelineGridWidth}
                $height={totalRowsHeight}
              >
                <MonthBackground />

                <TodayLine dateRange={dateRange} dayWidth={dayWidth} />

                {flattenedRows.map((item, index) =>
                  'goal' in item ? (
                    <Styled.GoalRow key={item.id} $top={rowTops[index]}>
                      <Styled.GoalRowInner>
                        {item.goal.startDate && item.goal.deadline && (
                          <GoalBar
                            goal={item.goal}
                            goals={goals}
                            lifeAreas={lifeAreas}
                            dateRange={dateRange}
                            dayWidth={dayWidth}
                            onUpdateDates={onUpdateDates}
                          />
                        )}

                        {(!item.goal.startDate || !item.goal.deadline) &&
                          placeholderState.visible &&
                          placeholderState.goalId === item.id && (
                            <Placeholder
                              dayWidth={dayWidth}
                              dayIndex={placeholderState.dayIndex}
                              durationDays={
                                PLACEHOLDER_DURATIONS_DAYS[timeframe]
                              }
                            />
                          )}
                      </Styled.GoalRowInner>
                    </Styled.GoalRow>
                  ) : null,
                )}
              </Styled.TimelineGrid>
            </Styled.TimelineBody>
          </Styled.Body>
        </Styled.BodyContainer>
      </Styled.Container>
    );
  },
);
